import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { IconLibrary, iconLibrary } from '@finnairoyj/fcom-ui-styles';

import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';

import { toKebabCase } from '../../../utils/string.utils';
import { type IconName } from '../icons.interfaces';

@Component({
  selector: 'fcom-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-testid]': 'dataTestIdValue()',
  },
})
export class IconComponent {
  private themeService = inject(FinnairThemeService);

  category = input<IconLibrary>(IconLibrary.SVG_LIBRARY);
  dataTestId = input<string>();
  name = input<IconName>(null);
  // when this is set to true, make sure the translations are available in cms
  informative = input(false);

  private errorThrown = false;
  private iconLibrary: typeof iconLibrary = iconLibrary;

  private effectiveCategory = computed((): IconLibrary => {
    const category = this.category() ?? IconLibrary.SVG_LIBRARY;

    if (this.themeService.siteTheme() !== FinnairTheme.LEGACY && category === IconLibrary.SVG_LIBRARY) {
      return IconLibrary.SVG_LIBRARY_NEW;
    }

    return category;
  });

  /** @default category-name-icon-name */
  dataTestIdValue = computed(
    () => this.dataTestId() || toKebabCase(`${this.effectiveCategory()}-${this.name() ?? 'unknown'}`)
  );

  iconRef = computed(() => `${this.effectiveCategory()}/${this.name()}`);

  svgPath = computed(() => {
    if (this.iconLibrary?.[this.effectiveCategory()]?.[this.name()]) {
      return this.iconLibrary[this.effectiveCategory()][this.name()];
    }

    this.throwSingleError(`Invalid Icon: ${String(this.effectiveCategory())}/${this.name()}`);
    return '';
  });

  // Throw an error in a timeout to stop it crashing other components but still report to sentry.
  throwSingleError(err: string): void {
    if (this.errorThrown) {
      return;
    }

    this.errorThrown = true;

    setTimeout(() => {
      throw new Error(err);
    }, 0);
  }
}
